import { useState } from 'react'
import { Link } from 'react-router-dom'
import Button from '../components/shared/Button'
import Card from '../components/shared/Card'
import Badge from '../components/shared/Badge'
import Spinner from '../components/shared/Spinner'
import Alert from '../components/shared/Alert'
import { useLatestRelease, useUserPlatform } from '../hooks/useDownloads'
import {
  Download,
  formatFileSize,
  getPlatformDisplayName,
} from '../api/downloads'
import './Download.css'

type Platform = 'MacOS' | 'Windows'

export default function DownloadPage() {
  const { platform: detectedPlatform } = useUserPlatform()
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>(() =>
    detectedPlatform === 'Linux' ? 'Windows' : detectedPlatform
  )
  const { release, isLoading, error } = useLatestRelease()

  // Find the download for the selected platform
  const currentDownload: Download | undefined = release?.downloads.find(
    (d) => d.platform.toLowerCase() === selectedPlatform.toLowerCase()
  )

  // Format the release date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Handle download click
  const handleDownload = () => {
    if (currentDownload?.downloadUrl) {
      window.location.href = currentDownload.downloadUrl
    }
  }

  return (
    <>
      {/* Hero */}
      <section className="download-hero">
        <div className="container">
          <h1 className="download-hero-title">Download PlanScan</h1>
          <p className="download-hero-subtitle">
            Available for macOS and Windows. Start your 14-day free trial today.
          </p>

          {/* Platform Toggle */}
          <div className="platform-toggle">
            <button
              className={`platform-btn ${selectedPlatform === 'MacOS' ? 'active' : ''}`}
              onClick={() => setSelectedPlatform('MacOS')}
            >
              <span className="platform-icon">🍎</span>
              macOS
            </button>
            <button
              className={`platform-btn ${selectedPlatform === 'Windows' ? 'active' : ''}`}
              onClick={() => setSelectedPlatform('Windows')}
            >
              <span className="platform-icon">🪟</span>
              Windows
            </button>
          </div>
        </div>
      </section>

      {/* Download Card */}
      <section className="download-section">
        <div className="container">
          {isLoading ? (
            <div className="download-loading">
              <Spinner size="large" showLabel label="Loading download info..." />
            </div>
          ) : error ? (
            <Alert variant="danger" className="download-error">
              {error}
            </Alert>
          ) : release ? (
            <Card className="download-card" variant="elevated">
              <div className="download-info">
                <div className="download-version">
                  <h2>PlanScan {release.version}</h2>
                  <Badge variant="success">
                    {release.isPreRelease ? 'Pre-release' : 'Latest'}
                  </Badge>
                  {release.channel !== 'Stable' && (
                    <Badge variant="warning">{release.channel}</Badge>
                  )}
                </div>
                <p className="download-meta">
                  Released {formatDate(release.publishedAt)}
                  {currentDownload && ` · ${formatFileSize(currentDownload.fileSizeBytes)}`}
                </p>

                {currentDownload ? (
                  <>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleDownload}
                    >
                      Download for {getPlatformDisplayName(selectedPlatform)}
                    </Button>

                    <details className="download-checksum">
                      <summary>Verify download (SHA256)</summary>
                      <code>{currentDownload.sha256}</code>
                    </details>
                  </>
                ) : (
                  <Alert variant="warning">
                    No download available for {getPlatformDisplayName(selectedPlatform)} yet.
                    Please check back later or try a different platform.
                  </Alert>
                )}
              </div>

              {release.releaseNotes && (
                <div className="download-notes">
                  <h3>What's New</h3>
                  <pre className="release-notes">{release.releaseNotes}</pre>
                </div>
              )}
            </Card>
          ) : (
            <Alert variant="warning">
              No releases available yet. Please check back soon.
            </Alert>
          )}
        </div>
      </section>

      {/* System Requirements */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">System Requirements</h2>
          <div className="features-grid">
            <Card className="requirements-card">
              <h3 className="feature-title">macOS</h3>
              <ul className="requirements-list">
                <li>macOS 12 (Monterey) or later</li>
                <li>Apple Silicon or Intel processor</li>
                <li>4 GB RAM minimum, 8 GB recommended</li>
                <li>500 MB available disk space</li>
              </ul>
            </Card>
            <Card className="requirements-card">
              <h3 className="feature-title">Windows</h3>
              <ul className="requirements-list">
                <li>Windows 10 version 1809 or later</li>
                <li>x64 processor (ARM not supported)</li>
                <li>4 GB RAM minimum, 8 GB recommended</li>
                <li>500 MB available disk space</li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Installation Help */}
      <section className="download-help">
        <div className="container text-center">
          <h2 className="section-title">Need Help Getting Started?</h2>
          <p className="hero-subtitle">
            Check out our getting started guide or contact support.
          </p>
          <div className="hero-cta">
            <Link to="/docs/getting-started" className="btn btn-primary">
              Getting Started Guide
            </Link>
            <a href="mailto:support@planscan.io" className="btn btn-secondary">
              Contact Support
            </a>
          </div>
        </div>
      </section>
    </>
  )
}
